import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaryCollapseComponent, BaryInputCheckboxComponent, BaryInputNumberComponent, BaryInputSelectComponent, BaryInputTextComponent } from '../../../../shared/components';
import { ObjLabelValue } from '../../../../../models/obj-label-value.interface';
import { LIST_MEASURE_UNIT, LIST_VEHICLE_CATEGORY, LIST_VEHICLE_TYPE, MeasureUnit, MeasureUnitEnum } from '../../../../../models/vehicle.model';
import { LIST_HANDLING_TYPE } from '../../../../../models/handling-type.model';
import { HandlingTypeEnum } from '../../../../../models/enums/handling-type.enum';
import { CommonModule } from '@angular/common';
import { IconsModule, MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';
import { TransportFormGroup } from '../../../../../models/forms/TransportFormGroup';
import { BaryIconComponent } from '../../../../shared/components/bary-icon/bary-icon.component';
import { FaDirectiveEnum } from '../../../../../models/enums/fa-directive.enum';
import { OfferServiceTypeEnum } from '../../../../../models/enums/offer-service-type.enum';
import { OfferMerchandiseTypeEnum } from '../../../../../models/enums/offer-merchandise-type.enum';

@Component({
  selector: 'jhi-transport-estimation-step',
  templateUrl: './transport-estimation-step.component.html',
  styleUrls: ['./transport-estimation-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BaryInputSelectComponent,
    BaryInputTextComponent,
    BaryInputNumberComponent,
    ReactiveFormsModule,
    IconsModule,
    MDBBootstrapModulesPro,
    BaryIconComponent,
    BaryInputCheckboxComponent,
    BaryCollapseComponent,
  ],
})
export class TransportEstimationStepComponent implements OnInit {
  readonly #translateService = inject(TranslateService);

  @Input() transportFormGroup!: TransportFormGroup;
  @Input() formGroup!: FormGroup;
  @Input() dimensionOpts: { label: string; value: any }[] = []; // "value" est de type number, c'est l'id de la dimension
  @Input() handlingTypes: Array<ObjLabelValue> = [];

  public categories: Array<ObjLabelValue> = Object.values(LIST_VEHICLE_CATEGORY).map(v => ({
    label: this.#translateService.instant(v.labelKey),
    value: v.code,
  }));

  public types!: Array<ObjLabelValue>;

  public weightUnit: Array<MeasureUnit> = [LIST_MEASURE_UNIT[MeasureUnitEnum.KG], LIST_MEASURE_UNIT[MeasureUnitEnum.TONNE]];
  public lengthUnit: Array<MeasureUnit> = [LIST_MEASURE_UNIT[MeasureUnitEnum.CENTIMETER], LIST_MEASURE_UNIT[MeasureUnitEnum.METER]];
  public volumeUnit: Array<MeasureUnit> = [LIST_MEASURE_UNIT[MeasureUnitEnum.CUBIC_METER]];
  public showSelect = true;
  public isCollapsed = true;

  protected readonly CRATE_OF_WINE = HandlingTypeEnum.CRATE_OF_WINE;
  protected readonly FAS = FaDirectiveEnum.FAS;

  public ngOnInit(): void {
    this.types = Object.values(LIST_VEHICLE_TYPE)
      .filter(
        v =>
          v.merchandiseType === this.transportFormGroup.controls.merchandiseType.value &&
          ((this.transportFormGroup.controls.isControlledTemperature.value && v.isControlledTemperature != false) ||
            (!this.transportFormGroup.controls.isControlledTemperature.value && !v.isControlledTemperature))
      )
      .map(v => ({
        label: this.#translateService.instant(v.labelKey),
        value: v.code,
      }));
    // Permet de forcer la mise à jour du label pour le choix des dimensions, mais pas très propre
    this.formGroup.controls['handlingType'].valueChanges.subscribe(_ => {
      this.showSelect = false;
      setTimeout(() => {
        this.showSelect = true;
      }, 1);
    });
  }

  public getControl(name: string) {
    return this.formGroup.get(name) as FormControl;
  }

  public get isFTL(): boolean {
    return OfferServiceTypeEnum.FTL === this.transportFormGroup.controls.serviceType.value;
  }

  public get isUnconditioned(): boolean {
    return OfferMerchandiseTypeEnum.UNCONDITIONED === this.transportFormGroup.controls.merchandiseType.value;
  }

  public getLabelKeyDimension(): string | null {
    return this.getControl('handlingType')?.value ? LIST_HANDLING_TYPE[this.getControl('handlingType')?.value]?.labelKeyDimension || null : null;
  }

  public getPlaceHolderKeyDimension(): string | null {
    return this.getControl('handlingType')?.value ? LIST_HANDLING_TYPE[this.getControl('handlingType')?.value]?.placeholderKeyDimension || null : null;
  }

  public changeCollapsed(event: boolean) {
    this.isCollapsed = event;
    if (this.isCollapsed) {
      this.getControl('length')?.disable();
      this.getControl('length')?.clearValidators();
      this.getControl('length')?.updateValueAndValidity();

      this.getControl('width')?.disable();
      this.getControl('width')?.clearValidators();
      this.getControl('width')?.updateValueAndValidity();

      this.getControl('maxHeight')?.disable();
      this.getControl('maxHeight')?.clearValidators();
      this.getControl('maxHeight')?.updateValueAndValidity();

      this.getControl('volume')?.enable();
      this.getControl('volume')?.setValidators([Validators.required, Validators.min(0.1)]);
      this.getControl('volume')?.updateValueAndValidity();
    } else {
      this.getControl('length')?.enable();
      this.getControl('length')?.setValidators([Validators.required, Validators.min(0.1)]);
      this.getControl('length')?.updateValueAndValidity();

      this.getControl('width')?.enable();
      this.getControl('width')?.setValidators([Validators.required, Validators.min(0.1)]);
      this.getControl('width')?.updateValueAndValidity();

      this.getControl('maxHeight')?.enable();
      this.getControl('maxHeight')?.setValidators([Validators.required, Validators.min(0.1)]);
      this.getControl('maxHeight')?.updateValueAndValidity();

      this.getControl('volume')?.disable();
      this.getControl('volume').setValue(undefined);
      this.getControl('volume')?.clearValidators();
      this.getControl('volume')?.updateValueAndValidity();
    }
  }
}
